import React, { useCallback, useRef, useState } from 'react';
import {
  ChevronRightIcon,
  LinkIcon,
  FolderIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { ResourceFolder, ResourceLink } from '../../types';
import { RESOURCE_MAX_DEPTH, ResourceTreeNode, folderIconClass, openResourceUrl } from './resourcesUtils';
import ResourceKindIcon from './ResourceKindIcon';
import { t } from '../../utils/translations';
import { IconButton } from '../ui/Button';
import RowActions from '../ui/RowActions';
import ExpandCollapse from '../common/ExpandCollapse';

const LINK_DRAG_MIME = 'application/x-resource-link';

interface ResourcesTreeProps {
  tree: ResourceTreeNode[];
  canManage: boolean;
  expandedIds: Set<string>;
  onToggleExpanded: (folderId: string) => void;
  onEnsureExpanded?: (folderId: string) => void;
  onAddSubfolder: (parent: ResourceFolder) => void;
  onAddLink: (folder: ResourceFolder) => void;
  onEditFolder: (folder: ResourceFolder) => void;
  onDeleteFolder: (folder: ResourceFolder) => void;
  onEditLink: (link: ResourceLink) => void;
  onDeleteLink: (link: ResourceLink) => void;
  onMoveLink?: (linkId: string, targetFolderId: string) => Promise<void>;
}

function VisibilityChips({ folder }: { folder: ResourceFolder }) {
  const chips: string[] = [];
  if (folder.allowCounselors) chips.push(t.resources.chipCounselors);
  if (folder.allowAdmins) chips.push(t.resources.chipAdmins);
  if (!folder.allowCounselors && !folder.allowAdmins) chips.push(t.resources.chipLeadersOnly);
  return (
    <span className="flex flex-wrap gap-1">
      {chips.map((label) => (
        <span
          key={label}
          className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600"
        >
          {label}
        </span>
      ))}
    </span>
  );
}

type FolderNodeProps = {
  node: ResourceTreeNode;
  depth: number;
  canManage: boolean;
  expandedIds: Set<string>;
  dragOverFolderId: string | null;
  draggingLinkId: string | null;
  onToggleExpanded: (folderId: string) => void;
  onAddSubfolder: (parent: ResourceFolder) => void;
  onAddLink: (folder: ResourceFolder) => void;
  onEditFolder: (folder: ResourceFolder) => void;
  onDeleteFolder: (folder: ResourceFolder) => void;
  onEditLink: (link: ResourceLink) => void;
  onDeleteLink: (link: ResourceLink) => void;
  onLinkDragStart: (link: ResourceLink, e: React.DragEvent) => void;
  onLinkDragEnd: () => void;
  onFolderDragOver: (folder: ResourceFolder, e: React.DragEvent) => void;
  onFolderDragLeave: (folderId: string, e: React.DragEvent) => void;
  onFolderDrop: (folder: ResourceFolder, e: React.DragEvent) => void;
};

const FolderNode: React.FC<FolderNodeProps> = ({
  node,
  depth,
  canManage,
  expandedIds,
  dragOverFolderId,
  draggingLinkId,
  onToggleExpanded,
  onAddSubfolder,
  onAddLink,
  onEditFolder,
  onDeleteFolder,
  onEditLink,
  onDeleteLink,
  onLinkDragStart,
  onLinkDragEnd,
  onFolderDragOver,
  onFolderDragLeave,
  onFolderDrop,
}) => {
  const { folder, children, links } = node;
  const open = expandedIds.has(folder.id);
  const canHaveSubfolder = depth + 1 < RESOURCE_MAX_DEPTH;
  const isDropTarget = dragOverFolderId === folder.id;
  const childProps = {
    canManage,
    expandedIds,
    dragOverFolderId,
    draggingLinkId,
    onToggleExpanded,
    onAddSubfolder,
    onAddLink,
    onEditFolder,
    onDeleteFolder,
    onEditLink,
    onDeleteLink,
    onLinkDragStart,
    onLinkDragEnd,
    onFolderDragOver,
    onFolderDragLeave,
    onFolderDrop,
  };

  const toggle = () => onToggleExpanded(folder.id);

  return (
    <div
      id={`resource-folder-${folder.id}`}
      className={depth === 0 ? 'rounded-xl border border-slate-200 bg-white shadow-sm' : ''}
    >
      <div
        onDragOver={canManage ? (e) => onFolderDragOver(folder, e) : undefined}
        onDragLeave={canManage ? (e) => onFolderDragLeave(folder.id, e) : undefined}
        onDrop={canManage ? (e) => onFolderDrop(folder, e) : undefined}
        className={`flex items-start gap-2 px-3 py-3 transition-colors sm:px-4 ${
          depth > 0 ? 'border-t border-slate-100' : ''
        } ${
          isDropTarget
            ? 'bg-brand-50 ring-2 ring-inset ring-brand-400'
            : ''
        }`}
      >
        <button
          type="button"
          onClick={toggle}
          className="mt-0.5 rounded p-0.5 text-slate-500 transition hover:bg-slate-100 hover:text-brand-700"
          aria-expanded={open}
          aria-label={open ? t.resources.collapse : t.resources.expand}
        >
          <ChevronRightIcon
            className={`h-4 w-4 transition-transform duration-300 ease-out motion-reduce:transition-none ${
              open ? 'rotate-90' : ''
            }`}
          />
        </button>
        <FolderIcon className={`mt-0.5 h-5 w-5 shrink-0 ${folderIconClass(folder.color)}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={toggle}
              className="text-left text-sm font-semibold text-slate-900"
            >
              {folder.name}
            </button>
            {canManage && <VisibilityChips folder={folder} />}
            {isDropTarget && (
              <span className="text-[11px] font-medium text-brand-700">
                {t.resources.dropLinkHere}
              </span>
            )}
          </div>
        </div>
        {canManage && (
          <RowActions
            onEdit={() => onEditFolder(folder)}
            onDelete={() => onDeleteFolder(folder)}
            leading={
              <>
                {canHaveSubfolder && (
                  <IconButton
                    icon={PlusIcon}
                    label={t.resources.addSubfolder}
                    onClick={() => onAddSubfolder(folder)}
                  />
                )}
                <IconButton
                  icon={LinkIcon}
                  label={t.resources.addLink}
                  onClick={() => onAddLink(folder)}
                />
              </>
            }
          />
        )}
      </div>

      <ExpandCollapse open={open}>
        <div
          className={depth === 0 ? 'pb-2' : ''}
          onDragOver={canManage ? (e) => onFolderDragOver(folder, e) : undefined}
          onDragLeave={canManage ? (e) => onFolderDragLeave(folder.id, e) : undefined}
          onDrop={canManage ? (e) => onFolderDrop(folder, e) : undefined}
        >
          {links.map((link) => {
            const isDragging = draggingLinkId === link.id;
            return (
              <div
                key={link.id}
                draggable={canManage}
                onDragStart={canManage ? (e) => onLinkDragStart(link, e) : undefined}
                onDragEnd={canManage ? onLinkDragEnd : undefined}
                className={`group flex items-center gap-2 border-t border-slate-50 px-3 py-2.5 hover:bg-slate-50 sm:px-4 ${
                  canManage ? 'cursor-grab active:cursor-grabbing' : ''
                } ${isDragging ? 'opacity-40' : ''}`}
                style={{ paddingLeft: `${1.75 + (depth + 1) * 0.75}rem` }}
              >
                <ResourceKindIcon kind={link.kind} />
                <button
                  type="button"
                  onClick={() => openResourceUrl(link.url)}
                  className="min-w-0 flex-1 truncate text-left text-sm font-medium text-slate-800 hover:text-brand-700"
                  title={t.resources.openInGoogle}
                >
                  {link.title}
                </button>
                {canManage && (
                    <RowActions
                      className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                      onEdit={() => onEditLink(link)}
                      onDelete={() => onDeleteLink(link)}
                    />
                )}
              </div>
            );
          })}

          {children.map((child) => (
            <div key={child.folder.id} style={{ paddingLeft: '0.75rem' }}>
              <FolderNode node={child} depth={depth + 1} {...childProps} />
            </div>
          ))}

          {links.length === 0 && children.length === 0 && (
            <p
              className="border-t border-slate-50 px-4 py-3 text-xs text-slate-400"
              style={{ paddingLeft: `${1.75 + (depth + 1) * 0.75}rem` }}
            >
              {t.resources.emptyFolder}
            </p>
          )}
        </div>
      </ExpandCollapse>
    </div>
  );
};

const ResourcesTree: React.FC<ResourcesTreeProps> = ({
  tree,
  canManage,
  expandedIds,
  onToggleExpanded,
  onEnsureExpanded,
  onAddSubfolder,
  onAddLink,
  onEditFolder,
  onDeleteFolder,
  onEditLink,
  onDeleteLink,
  onMoveLink,
}) => {
  const [draggingLinkId, setDraggingLinkId] = useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const expandTimerRef = useRef<number | null>(null);

  const clearExpandTimer = useCallback(() => {
    if (expandTimerRef.current != null) {
      window.clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }
  }, []);

  const handleLinkDragStart = useCallback((link: ResourceLink, e: React.DragEvent) => {
    e.dataTransfer.setData(LINK_DRAG_MIME, link.id);
    e.dataTransfer.setData('text/plain', link.id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingLinkId(link.id);
  }, []);

  const handleLinkDragEnd = useCallback(() => {
    clearExpandTimer();
    setDraggingLinkId(null);
    setDragOverFolderId(null);
  }, [clearExpandTimer]);

  const handleFolderDragOver = useCallback(
    (folder: ResourceFolder, e: React.DragEvent) => {
      if (!canManage || !onMoveLink) return;
      const types = Array.from(e.dataTransfer.types);
      if (!types.includes(LINK_DRAG_MIME) && !types.includes('text/plain')) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setDragOverFolderId(folder.id);

      if (!expandedIds.has(folder.id) && onEnsureExpanded) {
        clearExpandTimer();
        expandTimerRef.current = window.setTimeout(() => {
          onEnsureExpanded(folder.id);
        }, 450);
      }
    },
    [canManage, onMoveLink, expandedIds, onEnsureExpanded, clearExpandTimer]
  );

  const handleFolderDragLeave = useCallback((folderId: string, e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null;
    if (related && e.currentTarget.contains(related)) return;
    clearExpandTimer();
    setDragOverFolderId((current) => (current === folderId ? null : current));
  }, [clearExpandTimer]);

  const handleFolderDrop = useCallback(
    (folder: ResourceFolder, e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      clearExpandTimer();
      setDragOverFolderId(null);
      const linkId =
        e.dataTransfer.getData(LINK_DRAG_MIME) || e.dataTransfer.getData('text/plain');
      setDraggingLinkId(null);
      if (!linkId || !onMoveLink) return;
      void onMoveLink(linkId, folder.id).catch((err) => {
        console.error('Failed to move resource link:', err);
      });
    },
    [clearExpandTimer, onMoveLink]
  );

  const nodeProps = {
    canManage,
    expandedIds,
    dragOverFolderId,
    draggingLinkId,
    onToggleExpanded,
    onAddSubfolder,
    onAddLink,
    onEditFolder,
    onDeleteFolder,
    onEditLink,
    onDeleteLink,
    onLinkDragStart: handleLinkDragStart,
    onLinkDragEnd: handleLinkDragEnd,
    onFolderDragOver: handleFolderDragOver,
    onFolderDragLeave: handleFolderDragLeave,
    onFolderDrop: handleFolderDrop,
  };

  return (
    <div className="space-y-3">
      {tree.map((node) => (
        <FolderNode key={node.folder.id} node={node} depth={0} {...nodeProps} />
      ))}
    </div>
  );
};

export default ResourcesTree;
